import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Business, BusinessBranding } from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import BusinessHeader from '../components/BusinessHeader';
import BusinessFooter from '../components/BusinessFooter';
import TenantAskAiWidget from '../components/TenantAskAiWidget';

const LoginRequiredPage: React.FC = () => {
  const { businessShortName } = useParams<{ businessShortName: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [business, setBusiness] = useState<Business | null>(null);
  const [branding, setBranding] = useState<BusinessBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get redirect parameter from URL
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect') || `/${businessShortName}`;

  useEffect(() => {
    const fetchBusinessData = async () => {
      if (!businessShortName) return;
      
      try {
        setLoading(true);
        // Customer-facing: exclude expired businesses.
        const businessData = await apiService.getBusiness(businessShortName, { includeExpired: false });
        setBusiness(businessData.business);
        
        // Fetch branding data
        try {
          const brandingData = await apiService.getBusinessBranding(businessShortName);
          // Convert relative logo URL to full URL if needed
          if (brandingData?.logoUrl && typeof brandingData.logoUrl === 'string') {
            if (!brandingData.logoUrl.startsWith('http://') && !brandingData.logoUrl.startsWith('https://')) {
              const apiBaseUrl = process.env.REACT_APP_API_URL || (process.env.REACT_APP_ENV === 'production' ? 'https://booking-i73o.onrender.com/api' : 'http://localhost:3001/api');
              brandingData.logoUrl = `${apiBaseUrl}${brandingData.logoUrl.startsWith('/') ? brandingData.logoUrl : `/${brandingData.logoUrl}`}`;
            }
          }
          setBranding(brandingData);
        } catch (brandingError) {
          // Use default branding matching unified layout
          setBranding({
            logoUrl: undefined,
            primaryColor: '#e11d48',
            secondaryColor: '#9f1239',
            accentColor: '#f5c2cc',
            textColor: '#111827',
            backgroundColor: '#ffeef0',
            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            useCustomColors: false,
            layout: 'modern',
            galleryImages: []
          });
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load business information');
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessData();
  }, [businessShortName]);

  const handleLogin = () => {
    // Navigate to login with redirect parameter
    navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`);
  };

  const handleSignup = () => {
    // Navigate to register with customer type and redirect parameter
    navigate(`/register?type=customer&redirect=${encodeURIComponent(redirectUrl)}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Business Not Found</h1>
          <p className="text-gray-600 mb-6">{error || 'The requested business could not be found.'}</p>
          <Link 
            to="/login" 
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  // Extract colors from branding, with defaults matching the unified layout
  const brandColor = branding?.primaryColor || '#e11d48';
  const brandDark = branding?.secondaryColor || '#9f1239';
  const brandSoft = branding?.accentColor ? `${branding.accentColor}15` : '#fff1f2';
  const bgColor = branding?.backgroundColor || '#ffeef0';
  const surfaceColor = '#ffffff';
  const borderColor = branding?.accentColor ? `${branding.accentColor}40` : '#f5c2cc';
  const textColor = branding?.textColor || '#111827';
  const mutedColor = branding?.textColor ? `${branding.textColor}80` : '#6b7280';

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{
        backgroundColor: bgColor,
        fontFamily: branding?.fontFamily || 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        color: textColor
      }}
    >
      {/* Header */}
      <BusinessHeader
        business={business}
        branding={branding || undefined}
        surfaceColor={surfaceColor}
        borderColor={borderColor}
        textColor={textColor}
        mutedColor={mutedColor}
        brandColor={brandColor}
        brandSoft={brandSoft}
        brandDark={brandDark}
        showBookNowButton={false}
        showSignInButton={false}
      />

      <div className="flex-1">

      {/* Login Required Section */}
      <main className="py-4">
        <div className="container mx-auto px-4">
          <section className="py-8">
            <div className="max-w-2xl mx-auto">
              {/* Back to Business Link */}
              <Link
                to={`/${businessShortName}`}
                className="inline-flex items-center gap-2 mb-6 text-sm transition-colors hover:opacity-80"
                style={{ color: brandColor }}
              >
                <svg 
                  className="w-4 h-4" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M15 19l-7-7 7-7" 
                  />
                </svg>
                <span>Back to {business.name}</span>
              </Link>

              <div 
                className="rounded-2xl border p-7 shadow-lg"
                style={{
                  backgroundColor: surfaceColor,
                  borderColor: borderColor
                }}
              >
                <div className="text-center mb-8">
                  <div 
                    className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: brandColor }}
                  >
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h2 
                    className="text-3xl font-bold mb-4"
                    style={{ color: textColor }}
                  >
                    Login Required
                  </h2>
                  <p 
                    className="text-lg"
                    style={{ color: mutedColor }}
                  >
                    To book appointments with {business.name}, you need to sign in to your account.
                  </p>
                </div>

                <div className="space-y-6">
                  <div 
                    className="rounded-lg p-4"
                    style={{
                      backgroundColor: brandSoft,
                      borderColor: borderColor,
                      border: `1px solid ${borderColor}`
                    }}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="w-5 h-5" style={{ color: brandColor }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm" style={{ color: textColor }}>
                          <strong>Why do I need to sign in?</strong><br />
                          Signing in allows us to securely manage your appointments, send you confirmations, and provide you with the best service experience.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={handleLogin}
                      className="flex-1 px-6 py-3 rounded-lg font-medium transition-colors hover:opacity-90 text-white"
                      style={{ 
                        backgroundColor: brandColor,
                        borderColor: brandColor,
                        boxShadow: `0 8px 18px ${brandColor}4d`
                      }}
                    >
                      Sign In
                    </button>
                    <button
                      onClick={handleSignup}
                      className="flex-1 px-6 py-3 rounded-lg font-medium transition-colors hover:opacity-80 border"
                      style={{ 
                        borderColor: `${brandColor}59`,
                        color: brandColor,
                        backgroundColor: 'transparent'
                      }}
                    >
                      Create Account
                    </button>
                  </div>

                  <div className="text-center pt-6" style={{ borderTop: `1px solid ${borderColor}` }}>
                    <p 
                      className="text-sm"
                      style={{ color: mutedColor }}
                    >
                      Don't have an account?{' '}
                      <button
                        onClick={handleSignup}
                        className="font-medium underline bg-transparent border-none cursor-pointer p-0"
                        style={{ color: brandColor }}
                      >
                        Sign up for free
                      </button>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      </div>

      {/* Footer */}
      {business && (
        <BusinessFooter 
          business={business}
          branding={branding || undefined}
        />
      )}
      {business && (
        <TenantAskAiWidget
          shortName={business.shortName}
          businessName={business.name}
          enabled={business.askAiEnabled}
          showContactHint={business.showContactForm !== false}
          accentColor={brandColor}
          textColor={textColor}
        />
      )}
    </div>
  );
};

export default LoginRequiredPage; 